import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { FileText, Users, Mail, TrendingUp, ArrowUpRight } from "lucide-react";

export default function AdminDashboard() {
  const { authFetch } = useAdminAuth();
  const [stats, setStats] = useState({
    articles: null,
    subscribers: null,
    contacts: null,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      authFetch("get", "/admin/articles"),
      authFetch("get", "/admin/subscribers"),
      authFetch("get", "/admin/contacts"),
    ])
      .then(([a, s, c]) => {
        setStats({
          articles: a.data.count,
          subscribers: s.data.count,
          contacts: c.data.count,
        });
      })
      .catch(() => setError("Impossible de charger les statistiques."));
  }, [authFetch]);

  const tiles = [
    {
      label: "Articles publiés",
      value: stats.articles,
      icon: FileText,
      href: "/admin/articles",
      cta: "Gérer",
      id: "admin-stat-articles",
    },
    {
      label: "Abonnés newsletter",
      value: stats.subscribers,
      icon: Users,
      href: "/admin/subscribers",
      cta: "Voir la liste",
      id: "admin-stat-subscribers",
    },
    {
      label: "Demandes de contact",
      value: stats.contacts,
      icon: Mail,
      href: "/admin/contacts",
      cta: "Voir les demandes",
      id: "admin-stat-contacts",
    },
  ];

  return (
    <div data-testid="admin-dashboard-root">
      <div className="flex items-center gap-3 mb-4">
        <TrendingUp size={16} strokeWidth={1.5} className="text-[#b08d57]" />
        <span className="text-[10px] tracking-[0.32em] uppercase text-[#b08d57]">
          Vue d&apos;ensemble
        </span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-12">
        Tableau de bord
      </h1>

      {error && (
        <div className="mb-8 text-sm text-[#f0b74e] border border-[#f0b74e]/30 bg-[#f0b74e]/10 px-4 py-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiles.map((t) => (
          <Link
            key={t.id}
            to={t.href}
            data-testid={t.id}
            className="group relative border border-[#efeade]/10 bg-[#0a192f] p-8 hover:border-[#b08d57]/60 transition-colors"
          >
            <div className="flex items-center justify-between mb-8">
              <t.icon size={18} strokeWidth={1.5} className="text-[#b08d57]" />
              <ArrowUpRight
                size={18}
                strokeWidth={1.5}
                className="text-[#efeade]/40 group-hover:text-[#b08d57] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
              />
            </div>
            <div className="font-serif text-5xl mb-1 tabular-nums">
              {t.value ?? "—"}
            </div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#efeade]/60">
              {t.label}
            </div>
            <div className="mt-5 text-xs tracking-[0.22em] uppercase text-[#b08d57] group-hover:text-[#f0b74e]">
              {t.cta}
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-16 p-8 border border-[#b08d57]/25 bg-[#b08d57]/5">
        <h2 className="font-serif text-2xl mb-3">Astuces</h2>
        <ul className="space-y-2 text-sm text-[#efeade]/70 leading-relaxed">
          <li>· Les articles créés sont immédiatement visibles sur <Link to="/actualites" className="underline text-[#f0b74e]">/actualites</Link>.</li>
          <li>· Les promotions affichent automatiquement un countdown si vous renseignez « Fin de l&apos;offre ».</li>
          <li>· Un email de bienvenue avec le livre blanc est envoyé automatiquement aux nouveaux abonnés (dès que la clé Resend est configurée).</li>
        </ul>
      </div>
    </div>
  );
}
